import React, { useCallback, useEffect, useRef } from 'react';
import { Card, Tag, Typography, Divider, Carousel, Tooltip } from 'antd';
import { ClockCircleOutlined, InfoCircleOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import './style.scss';
import { ActivityDates, DetailLabel } from '../ActivityCheckout';
import moment from 'moment';
import { IRoom } from '@share/common-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { renderNumber, GetEffectiveClientCashPercentage, RootState, AppDispatch } from '@share/utils';
import { HotelStars, WalletInput, WalletRewardInput, WalletMessage } from '@components';
import { getWalletPrices, WalletPriceType, getRewardByPriority } from '@utils';
import type { IAccount, IUserWallet } from '@share/common-types';
import { setSelectedActivityReviewClientCash, setSelectedActivityReviewClientCashStr, setReviewBookRewardId, activityActions } from '@share/store/slices';

const { Title, Text } = Typography;

interface BookingSummaryProps {
    detailLabel: DetailLabel;
    dates: ActivityDates;
    roomsSelected?: IRoom[];
    onWalletValidityChange?: (isValid: boolean) => void;
}

export const BookingSummary: React.FC<BookingSummaryProps> = ({ detailLabel, dates, roomsSelected, onWalletValidityChange }) => {
    const intl = useIntl();
    const dispatch = useDispatch<AppDispatch>();

    const loginStore = useSelector((s: RootState) => s.loginStore);
    const activityStore = useSelector((s: RootState) => s.activityStore);
    const activityCheckoutStore = useSelector((s: RootState) => s.activityCheckoutStore);
    const menuStore = useSelector((s: RootState) => s.navigationMenuStore);

    const { activity } = useSelector((state: RootState) => state.activityDetailsStore);
    const { selectedDate } = useSelector((state: RootState) => state.activityAvailabilityStore);

    // Prefer the chosen operation day (HotelBeds); fall back to the search window start (Viator / no selection).
    const bookingDate = selectedDate || dates?.startDate;

    const { account, userWallet, userWalletData } = loginStore;
    const { selectedActivitySearchClientCash } = activityStore;
    const {
        selectedActivityReviewClientCash,
        selectedActivityReviewClientCashStr,
        rewardId,
        success: bookingComplete,
    } = activityCheckoutStore;
    const { items } = menuStore;

    const hasClientCash = !!account?.hasClientCash;
    const isLogged = !!userWallet;
    const isMLM = !!items?.isMLM;

    const balanceInt = detailLabel.price ?? 0;
    const taxesAmount = detailLabel.taxes ?? 0;
    // Pre-tax base price (price − taxes), serialized by the BFF as `priceWithoutTaxes`. Like Hotels,
    // the wallet/points discount cap is a percentage of this base (not of the tax-inclusive `price`),
    // so it's what we pass as the wallet basis and render as "Total Price". Fall back to the
    // tax-inclusive price when the BFF doesn't provide it.
    const priceWithoutTaxes = detailLabel.priceWithoutTaxes ?? 0;
    const payNowPriceWithoutTaxesInt = priceWithoutTaxes
        ? Math.round(priceWithoutTaxes * 100) / 100
        : (detailLabel.price ?? 0);
    const maxWalletClientCashFromRate = detailLabel.maxWalletClientCash ?? 0;
    const convertionRate = userWalletData?.convertionRate ?? 1;
    const maxWalletClientCashValueInt = maxWalletClientCashFromRate / convertionRate;
    const maxWalletClientCashValue = account?.walletNoDecimals
        ? Math.floor(maxWalletClientCashValueInt)
        : maxWalletClientCashValueInt;

    const walletPrices = getWalletPrices(
        account as IAccount,
        userWalletData as IUserWallet,
        selectedActivitySearchClientCash,
        payNowPriceWithoutTaxesInt,
        maxWalletClientCashValue,
        WalletPriceType.Book
    );

    const displayWalletSavings =
        !!account?.walletWalletSavings &&
        (!!(walletPrices?.priceSavings && walletPrices.priceSavings > 0) ||
            !!(walletPrices?.maxClientCashAmountAllow && walletPrices.maxClientCashAmountAllow > 0)) &&
        !!walletPrices?.maxClientCashAmountAllow;

    const maxWalletClientCashService = walletPrices?.maxClientCashAllow ?? 0;
    const maxWalletClientCashFinal =
        maxWalletClientCashValue && maxWalletClientCashValue <= maxWalletClientCashService
            ? maxWalletClientCashValue
            : maxWalletClientCashService;
    const maxWalletClientCashCalculated = account?.walletNoDecimals
        ? Math.floor(maxWalletClientCashFinal)
        : maxWalletClientCashFinal;

    // Carry the client cash the user set with the search slider into checkout (mirrors hotels/condos).
    // Seed the review value once from the search selection when checkout still has none, clamped to
    // the amount actually allowed for this rate.
    const didSeedReviewClientCash = useRef(false);
    useEffect(() => {
        if (didSeedReviewClientCash.current || bookingComplete) {
            return;
        }
        const searchClientCash = selectedActivitySearchClientCash?.selectedSearchClientCash ?? 0;
        const canUseClientCash = hasClientCash && !account?.walletIsExternal && isLogged;
        if (canUseClientCash && (selectedActivityReviewClientCash ?? 0) <= 0 && searchClientCash > 0 && maxWalletClientCashCalculated > 0) {
            const seed = Math.min(searchClientCash, maxWalletClientCashCalculated);
            didSeedReviewClientCash.current = true;
            dispatch(setSelectedActivityReviewClientCash(seed));
            dispatch(setSelectedActivityReviewClientCashStr(seed ? seed.toString() : ''));
            // Also mirror the seed onto the shared client-cash object so the WalletMessage blue box
            // (WalletPriceType.Book reads selectedPropertyReviewClientCash) shows the applied discount
            // on first load, not only after the slider is moved.
            dispatch(activityActions.setSelectedActivitySearchClientCash({
                ...selectedActivitySearchClientCash,
                selectedPropertyReviewClientCash: seed,
            }));
        }
    }, [selectedActivitySearchClientCash, selectedActivityReviewClientCash, maxWalletClientCashCalculated, hasClientCash, isLogged, bookingComplete, account?.walletIsExternal, dispatch]);

    const clientCashInPriceCurrency = selectedActivityReviewClientCash * convertionRate;
    const finalBalance = Math.max(0, balanceInt - clientCashInPriceCurrency);
    const rewardDeduction = rewardId && detailLabel.maxRewardWalletAmount ? detailLabel.maxRewardWalletAmount : 0;
    const totalPayDisplay = Math.max(0, finalBalance - rewardDeduction);

    const effectiveClientCashPercentage = GetEffectiveClientCashPercentage(loginStore);
    const showEligibleClientCash = hasClientCash && !account?.walletIsExternal && !isMLM && effectiveClientCashPercentage > 0;
    const eligibleClientCash = finalBalance > 0 ? finalBalance * effectiveClientCashPercentage / 100 : 0;

    // Applying wallet client cash turns the booking non-refundable (mirrors hotels/cars). A wallet
    // profile with SkipNonRefundable opts out: the rate keeps the provider's real refundability.
    const usingClientCashNonRefundable =
        !account?.walletSkipNonRefundable &&
        !account?.walletIsExternal && hasClientCash && isLogged && (selectedActivityReviewClientCash ?? 0) > 0;
    const isRefundable = detailLabel.refundable && !usingClientCashNonRefundable;

    // Only show the public price (and its savings line / crossed-out total) when there is a real saving.
    const hasSavings = (detailLabel.marginatorSavings ?? 0) > 0;

    const handleOnSelectClientCash = useCallback(
        (clientCash: string | null | undefined) => {
            const num = clientCash != null && clientCash !== '' ? Number(clientCash) : 0;
            dispatch(setSelectedActivityReviewClientCash(num));
            dispatch(setSelectedActivityReviewClientCashStr(clientCash ?? ''));
            // Keep the review client cash on the shared search-client-cash object too, so the
            // WalletMessage (which reads it via getWalletPrices for the Book type) updates the
            // "applied as discount" line as the slider moves, leaving the Rate untouched.
            dispatch(activityActions.setSelectedActivitySearchClientCash({
                ...selectedActivitySearchClientCash,
                selectedPropertyReviewClientCash: num,
            }));
        },
        [dispatch, selectedActivitySearchClientCash]
    );

    const handleOnSelectReward = useCallback(
        (selected: boolean) => {
            const allRewards = loginStore?.userWalletReward?.allRewards;
            const nextAvailableReward = loginStore?.userWalletReward?.nextAvailableReward;
            const priority = account?.rewardWalletPriority;
            const maxRewardWalletAmount = detailLabel.maxRewardWalletAmount;
            const reward = getRewardByPriority(allRewards, priority, maxRewardWalletAmount, nextAvailableReward);
            dispatch(setReviewBookRewardId(selected && reward?.rewardId != null ? reward.rewardId : null));
        },
        [dispatch, loginStore?.userWalletReward?.allRewards, loginStore?.userWalletReward?.nextAvailableReward, account?.rewardWalletPriority, detailLabel.maxRewardWalletAmount]
    );

    const formatPrice = (price: number, currency?: string) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency || "USD",
            minimumFractionDigits: 2,
        }).format(price);
    };

    const modalities = activity?.modalities;
    const rate = modalities?.[0]?.rates?.[0];
    const cancellationPolicies = rate?.cancellationPolicies;
    // The badge uses `isRefundable` (override applied); this drives the tooltip beside it, so it
    // has to apply the same override or the two disagree.
    const refundability = rate?.refundable && !usingClientCashNonRefundable;

    const getCancellationTooltipContent = () => {
        if (refundability) {
            if (rate?.cancellationPolicyText) {
                return rate.cancellationPolicyText;
            }
            if (cancellationPolicies && cancellationPolicies.length > 0) {
                return (
                    <div className="tooltip-cancellation-policies" style={{ textAlign: 'left' }}>
                        {cancellationPolicies.map((policy: any, idx: number) => {
                            const isFree = Number(policy.amount || 0) === 0;
                            const policyDate = policy.from || policy.dateFrom;
                            const hasValidDate = policyDate && !policyDate.startsWith("0001");
                            const formattedDate = hasValidDate
                                ? intl.formatDate(new Date(policyDate), { year: 'numeric', month: 'long', day: 'numeric' })
                                : '';
                            return (
                                <div key={idx} style={{ marginBottom: idx < cancellationPolicies.length - 1 ? '4px' : '0px' }}>
                                    {isFree ? (
                                        <FormattedMessage
                                            id="activity.details.freeCancellationUntil"
                                            defaultMessage="Free cancellation until {date}."
                                            values={{ date: <strong>{formattedDate}</strong> }}
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="activity.details.cancellationFeeApply"
                                            defaultMessage="A fee of {amount} will apply for cancellations made on or after {date}."
                                            values={{
                                                amount: <strong>{policy.currency || rate.currency || detailLabel.currency || 'USD'} {Number(policy.amount || 0).toFixed(2)}</strong>,
                                                date: <strong>{formattedDate}</strong>
                                            }}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            }
            return null;
        } else {
            // A forced non-refundable booking must not show the rate's refundable prose either, so
            // skip it and fall through to the non-refundable message below.
            return (usingClientCashNonRefundable ? null : rate?.cancellationPolicyText) || intl.formatMessage({
                id: 'cancellation.not_refundable.message.policy',
                defaultMessage: 'In the event of no-show, your booking will not qualify for a refund.\n There are no cancellation or modifications allowed for this reservation.'
            });
        }
    };

    const cancellationTooltipContent = getCancellationTooltipContent();
    const hasCancellationTooltip = !!cancellationTooltipContent;
  
    return (
        <div className="booking-summary">
            {/* Activity Summary */}
            <Card className="summary-card">
                <div className="activity-image">
                    <Carousel
                        autoplay
                        arrows
                        nextArrow={<RightOutlined />}
                        prevArrow={<LeftOutlined />}
                    >
                        {detailLabel.mediaImages && detailLabel.mediaImages.length > 0 ? (
                            detailLabel.mediaImages.map((image, index) => (
                                <div key={index}>
                                    <img
                                        src={image.resource}
                                        alt={`${detailLabel.name} - ${index + 1}`}
                                    />
                                </div>
                            ))
                        ) : (
                            <div>
                                <img
                                    src="https://via.placeholder.com/300x200?text=No+Image"
                                    alt={detailLabel.name}
                                />
                            </div>
                        )}
                    </Carousel>
                    {detailLabel.marginatorsavePercents > 0 && (
                        <Tag color="red" className="savings-tag">
                            <FormattedMessage id="save" defaultMessage="Save" /> {detailLabel.marginatorsavePercents}%
                        </Tag>
                    )}
                </div>


                <div className="activity-info">
                    <Title level={4}>{detailLabel.name}</Title>
                    {(detailLabel.review ?? 0) > 0 && (
                        <div className="rating">
                            <HotelStars stars={detailLabel.rating} />
                            <Text>{renderNumber(detailLabel.rating, 2)} ({detailLabel.review} <FormattedMessage id="reviews" values={{ count: detailLabel.review }} defaultMessage="reviews" />)</Text>
                        </div>
                    )}

                    <div className="activity-details">
                        <div><Text strong><FormattedMessage id='bookingSummary.duration' defaultMessage='Duration:' /> {detailLabel.duration}</Text></div>
                        <div className='refundable-booking-tag' > {isRefundable ? (
                            <Tag color="cyan" className="status-tag refundable">
                                <FormattedMessage id='refundable.type' defaultMessage='Refundable' />
                                {hasCancellationTooltip && (
                                    <Tooltip title={cancellationTooltipContent}>
                                        <InfoCircleOutlined style={{ marginLeft: '5px' }} />
                                    </Tooltip>)}
                            </Tag>
                        ) : (
                            <Tag color="red" className="status-tag non-refundable">
                                <FormattedMessage id='non.refundable' defaultMessage='Non-Refundable' />
                                {hasCancellationTooltip && (
                                    <Tooltip title={cancellationTooltipContent}>
                                        <InfoCircleOutlined style={{ marginLeft: '5px' }} />
                                    </Tooltip>)}
                            </Tag>
                        )}</div>
                    </div>
                    <div className="booking-dates">
                        {bookingDate && (
                            <div>
                                <Text strong>
                                    <FormattedMessage id="bookingSummary.date" defaultMessage="Date:" />
                                </Text>{' '}
                                {moment(bookingDate).format('DD MMM YYYY')}
                            </div>
                        )}
                        {detailLabel.time && (
                            <div>
                                <Text strong>
                                    <FormattedMessage id="bookingSummary.time" defaultMessage="Time:" />
                                </Text>{' '}
                                {detailLabel.time}
                            </div>
                        )}
                        <div><Text strong><FormattedMessage id="bookingSummary.guests" defaultMessage="Guests:" /></Text> {
                            roomsSelected?.map((room, index) => {
                                const guestInfo = [];
                                if (room.adultsCount > 0) guestInfo.push(`${room.adultsCount} ${intl.formatMessage({ id: 'adult', defaultMessage: 'Adult' }, { count: room.adultsCount })}`);
                                if (room.kids && room.kids.length > 0) guestInfo.push(`${room.kids.length} ${intl.formatMessage({ id: 'child', defaultMessage: 'Child' }, { count: room.kids.length })}`);
                                return <span key={index}>{guestInfo.join(', ')}{index < roomsSelected.length - 1 ? ' | ' : ''}</span>
                            }) || <FormattedMessage id="bookingSummary.onePerson" defaultMessage="1 Person" />
                        }</div>
                    </div>
                </div>
            </Card>

            {/* Price Details */}
            <Card className="price-card">
                <Title level={4}><FormattedMessage id="bookingSummary.priceDetails" defaultMessage="Price Details" /></Title>

                <div className="price-breakdown">
                    {hasSavings && (
                        <>
                            <div className="price-row">
                                <Text><FormattedMessage id="public.price" defaultMessage="Public price:" />:</Text>
                                <Text>{formatPrice(detailLabel.pubPrice || 0, detailLabel.currency)} <FormattedMessage id="activity.details.usd" defaultMessage="USD" /></Text>
                            </div>
                            <div className="price-row savings">
                                <Text><FormattedMessage id="savings" defaultMessage="Savings" />:</Text>
                                <Text>-{formatPrice(detailLabel.marginatorSavings || 0, detailLabel.currency)} <FormattedMessage id="activity.details.usd" defaultMessage="USD" /></Text>
                            </div>
                        </>
                    )}
                    {priceWithoutTaxes > 0 && (
                        <div className="price-row">
                            <Text><FormattedMessage id="price.details.total_price" defaultMessage="Total Price" /></Text>
                            <Text>{formatPrice(priceWithoutTaxes, detailLabel.currency)} <FormattedMessage id="activity.details.usd" defaultMessage="USD" /></Text>
                        </div>
                    )}
                    {taxesAmount > 0 && (
                        <div className="price-row">
                            <Text><FormattedMessage id="price.details.taxes" defaultMessage="Taxes" /></Text>
                            <Text>{formatPrice(taxesAmount, detailLabel.currency)} <FormattedMessage id="activity.details.usd" defaultMessage="USD" /></Text>
                        </div>
                    )}
                    {showEligibleClientCash && (
                        <div className="price-row eligible-client-cash">
                            <Text>
                                <FormattedMessage id="wallet.client.cash.eligible" values={{ value: effectiveClientCashPercentage, clientCashName: account?.walletClientCashName }} />
                                <Tooltip title={intl.formatMessage({ id: 'wallet.client.cash.eligible.tooltip' })} placement="top">
                                    <InfoCircleOutlined style={{ marginLeft: 5 }} />
                                </Tooltip>
                            </Text>
                            <Text>{formatPrice(eligibleClientCash, detailLabel.currency)} <FormattedMessage id="activity.details.usd" defaultMessage="USD" /></Text>
                        </div>
                    )}

                    <WalletInput
                        display={((hasClientCash && isLogged && !bookingComplete) || (isMLM && !bookingComplete)) as boolean}
                        bottomBorder
                        currency={detailLabel.currency}
                        maxWalletClientCash={maxWalletClientCashCalculated}
                        price={payNowPriceWithoutTaxesInt}
                        selectedClientCash={selectedActivityReviewClientCash}
                        selectedClientCashStr={selectedActivityReviewClientCashStr}
                        onSelectClientCash={handleOnSelectClientCash}
                        onValidityChange={onWalletValidityChange}
                    />
                    <WalletRewardInput
                        display={((isLogged && !bookingComplete) || (isMLM && !bookingComplete)) as boolean}
                        onSelectReward={handleOnSelectReward}
                        currency={detailLabel.currency}
                        maxRewardCash={detailLabel.maxRewardWalletAmount}
                        isEligibleForRewardWallet={detailLabel.isEligibleForRewardWallet}
                    />

                    {!displayWalletSavings ? (
                        <WalletMessage
                            price={balanceInt}
                            maxClientCash={maxWalletClientCashCalculated}
                            currency={detailLabel.currency}
                            type={WalletPriceType.Book}
                            displayOnlyRate={bookingComplete}
                            isOneLine
                            style={{ paddingLeft: '15px', paddingRight: '15px', marginBottom: '15px' }}
                        />) : null}

                    {rewardId && detailLabel.maxRewardWalletAmount ? (
                        <div className="price-row price-row--reward-applied">
                            <Text><FormattedMessage id="wallet.reward.reward_applied" defaultMessage="Reward applied" />:</Text>
                            <Text>-{formatPrice(detailLabel.maxRewardWalletAmount, detailLabel.currency)} <FormattedMessage id="activity.details.usd" defaultMessage="USD" /></Text>
                        </div>
                    ) : null}

                    <Divider />
                    
                    <div className="price-row total">
                        <Text strong><FormattedMessage id="total.pay" defaultMessage="Total pay:" /></Text>
                        <div className="total-price">
                            {hasSavings && (
                                <Text delete className="original-price">{formatPrice(detailLabel.pubPrice || 0, detailLabel.currency)} <FormattedMessage id="activity.details.usd" defaultMessage="USD" /></Text>
                            )}
                            <Text strong className="final-price">{formatPrice(totalPayDisplay, detailLabel.currency)} <FormattedMessage id="activity.details.usd" defaultMessage="USD" /></Text>
                        </div>
                    </div>
                    {taxesAmount > 0 && (
                        <Text className="taxes-note">
                            <FormattedMessage
                                id="price.details.including_taxes"
                                defaultMessage="Including taxes {currency} {amount}"
                                values={{ currency: detailLabel.currency || 'USD', amount: renderNumber(taxesAmount, 2) }}
                            />
                        </Text>
                    )}
                </div>

                <div className="urgency-note">
                    <ClockCircleOutlined />
                    <Text><FormattedMessage id="booking.terms.charge" defaultMessage="Act fast! Pricing and availability may change" /></Text>
                </div>
            </Card>
        </div>
    );
};
